/**
 * Activities — `docs/API.md` §8.
 *
 * Fully implemented server-side: list (+ filter-tree query), create, read,
 * patch, complete, delete and restore all live in `salescrmapi/src/modules/
 * activities`. `bulk-complete`, `reschedule`, a standalone `participants`
 * endpoint and `attachments`/`recording` answer `501 not_implemented` — none
 * of those are exposed here.
 *
 * `GET /activities?lead_id=<id>` is how a lead's Activity tab reads its own
 * timeline; there is no `/leads/{id}/activities` sub-resource; the polymorphic
 * link runs the other way, as a nullable `lead_id` FK on `crm.activity`.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listActivitiesQuery's fields, e.g. `{ lead_id }` */
export function listActivities(params = {}) {
  return withMockFallback(
    'GET /activities',
    async () => (await apiClient.get('/activities', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}

/**
 * The calendar window — `GET /activities/calendar`. Capped server-side at 92
 * days between `from`/`to`; defaults to the caller's own calendar, widened by
 * `user_ids` only as far as the caller's `activities` scope (not `calendar`'s
 * own scope — see the route's own doc comment) actually allows.
 * @param {{from:string, to:string, user_ids?:string, kinds?:string}} params
 */
export function getCalendar(params) {
  return withMockFallback(
    'GET /activities/calendar',
    async () => (await apiClient.get('/activities/calendar', { params })).data,
    () => ({ window: { from: params.from, to: params.to, days: 0 }, recurrence_expanded: false, data: [] }),
  );
}

/** @param {string} id */
export function getActivity(id) {
  return withMockFallback(
    `GET /activities/${id}`,
    async () => (await apiClient.get(`/activities/${id}`)).data,
    () => null,
  );
}

/**
 * Create an activity. Carries an Idempotency-Key so a double-submit cannot
 * log the same call twice.
 * @param {object} payload one of `lead_id`/`account_id`/`opportunity_id`/
 *   `quotation_id`/`campaign_id` is required — the database CHECK enforces it.
 */
export function createActivity(payload) {
  return withMockFallback(
    'POST /activities',
    async () =>
      (await apiClient.post('/activities', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: `AC-${Math.floor(Math.random() * 90000 + 10000)}`, row_version: 1 }),
  );
}

/**
 * Update an activity under optimistic locking (§1.7).
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export function updateActivity(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /activities/${id}`,
    async () =>
      (await apiClient.patch(`/activities/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Log the outcome and close an activity. Refuses on an already-completed one
 * (`409 already_completed`) and can create a linked follow-up in the same
 * transaction via `body.next_activity`.
 * @param {string} id
 * @param {{outcome?:string, outcome_note?:string, duration_minutes?:number,
 *          next_activity?:object, rowVersion?:number}} [options]
 */
export function completeActivity(id, { rowVersion, ...body } = {}) {
  return withMockFallback(
    `POST /activities/${id}/complete`,
    async () =>
      (await apiClient.post(`/activities/${id}/complete`, {
        ...body, ...(rowVersion ? { row_version: rowVersion } : {}),
      }, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ id, state: 'completed', completed_at: new Date().toISOString(), ...body }),
  );
}

/**
 * Soft-delete an activity. `reason` is mandatory, not encouraged.
 * @param {string} id
 * @param {{reason:string, rowVersion?:number}} options
 */
export function deleteActivity(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /activities/${id}`,
    async () =>
      (await apiClient.delete(`/activities/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** Undo a soft delete. @param {string} id */
export function restoreActivity(id) {
  return withMockFallback(
    `POST /activities/${id}/restore`,
    async () => (await apiClient.post(`/activities/${id}/restore`)).data,
    () => ({ id, deleted: false }),
  );
}
